import React from 'react';
import { Alert, Segmented } from 'antd';

import { COMMENT, LOSE } from './changeCardModel';
import {
	ADD_TO_COMMENT,
	FILE_HELP,
	LOSE_HELP,
	LOSE_LABEL,
	commentHelp,
	leftBehindIntro,
	leftBehindSentence,
	restrictedHelp,
	warningText,
} from './reviewCopy';

const CHOICES = [
	{ value: COMMENT, label: ADD_TO_COMMENT },
	{ value: LOSE, label: LOSE_LABEL },
];

/**
 * Data left behind (KPR-707; the epic's rulings 6–8): every source element the
 * person can read that holds data and that nothing maps, from the dry run's
 * `unmapped`. Each is added to one comment per item (the default) or lost,
 * kept in History only. An element an element rule governs can only be lost —
 * a comment is read by everyone who reads the item — and a file stays on the
 * item as its attachments. Elements the person cannot read never reach this
 * list: the dry run leaves them out.
 *
 * Drawn on the mapping and again on the review step (KPR-709), where each
 * row says how many items hold a value that is not moved and the choice can
 * still be changed. `warnings` are drawn here on the mapping only; the review
 * draws its own (ReviewPanels' WarningsPanel). `ctx` names the cards.
 */
export default function LeftBehindPanel({ unmapped = [], warnings = [], onChange, ctx }) {
	if (!unmapped.length && !warnings.length) return null;
	return (
		<section className="change-card__panel change-card__left-behind" aria-label="Left behind">
			<h2 className="change-card__panel-title">Left behind</h2>
			{unmapped.length ? (
				<p className="change-card__note">{leftBehindIntro(ctx)}</p>
			) : null}
			<ul className="change-card__left-list">
				{unmapped.map(row => {
					const label = row.label || row.element;
					const restricted = row.choice === LOSE && !row.chosen;
					const choice = row.choice === LOSE ? LOSE : COMMENT;
					return (
						<li key={row.element} className="change-card__left-row">
							<span className="change-card__left-name">
								{leftBehindSentence(row.holding, label)}
							</span>
							{row.choice === 'attachment' ? (
								<span className="change-card__note">{FILE_HELP}</span>
							) : restricted ? (
								<span className="change-card__note">{restrictedHelp(label)}</span>
							) : (
								<span className="change-card__left-choice">
									<Segmented
										size="small"
										options={CHOICES}
										value={choice}
										onChange={value => onChange(row.element, value)}
										aria-label={`What happens to ${label}`}
									/>
									<span className="change-card__note">{choice === LOSE ? LOSE_HELP : commentHelp(label, ctx)}</span>
								</span>
							)}
						</li>
					);
				})}
			</ul>
			{warnings.map(warning => {
				const shown = warningText(warning, ctx);
				return shown.text ? (
					<Alert key={warning.code} type="warning" showIcon message={shown.text} className="change-card__warning" />
				) : null;
			})}
		</section>
	);
}
